/**
 * Quick search over the whole archive, opened as a command palette from the
 * nav, from the ticket list's search bar, or with Cmd+K / Ctrl+K anywhere in
 * the sidebar layout. The highlighted result is previewed beside the list;
 * picking it opens the conversation in the agent route.
 */
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { History, MessageCircleQuestion, X } from "lucide-react";
import { MATCH_TYPE_META, renderSnippet, Throbber } from "@lytra/frontend-common";
import {
  CommandDialog,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useDebounce } from "@/components/MultipleSelector.utils";
import { useFeature, useProfile } from "@/providers/ProfileProvider";
import { cn } from "@/lib/utils";
import Ticket from "@/components/customer/ticket/Ticket";
import {
  agentConversationPath,
  useArchiveSearch,
} from "@/components/customer/archive/useArchiveSearch";
import { useRecentSearches } from "@/components/customer/archive/useRecentSearches";
import {
  ArchiveSearchDialogContext,
  type ArchiveSearchDialogControls,
} from "@/components/customer/archive/archiveSearchDialogContext";
import { KBD_CLASS } from "@/lib/kbd";

// Keyboard selection reads as a quiet hover, like a quick-search row, not as
// an emphasised action: the theme's accent is the brand purple.
const PALETTE_ITEM_CLASS =
  "data-[selected=true]:bg-muted data-[selected=true]:text-foreground";

const ASK_ASSISTANT_VALUE = "ask-assistant";

interface PreviewTarget {
  conversationId: string;
  matchedMessageId: string | null;
}

export function ArchiveSearchDialogProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { userRoles } = useProfile();
  const featureTicketing = useFeature("ticketing");
  const [isOpen, setIsOpen] = useState(false);
  const [initialQuery, setInitialQuery] = useState("");
  const [openCount, setOpenCount] = useState(0);
  const [selectedValue, setSelectedValue] = useState("");

  const open = useCallback((query?: string) => {
    setInitialQuery(query?.trim() ?? "");
    setSelectedValue("");
    setOpenCount((count) => count + 1);
    setIsOpen(true);
  }, []);

  // Cmd/Ctrl+K. Cmd/Ctrl+F is left to the browser's find on purpose: it searches the
  // page in front of the agent, which the palette cannot replace. Same gate as the nav
  // item: only agents, and only where the conversation browser (feature.ticketing)
  // exists, or for staff.
  const shortcutEnabled =
    userRoles.isAgent && (userRoles.isLytraStaff || featureTicketing);
  useEffect(() => {
    if (!shortcutEnabled) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.shiftKey || event.altKey) return;
      if (event.key.toLowerCase() !== "k") return;
      event.preventDefault();
      if (!isOpen) open();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, open, shortcutEnabled]);

  const controls = useMemo<ArchiveSearchDialogControls>(() => ({ open }), [open]);

  return (
    <ArchiveSearchDialogContext value={controls}>
      {children}
      <CommandDialog
        open={isOpen}
        onOpenChange={setIsOpen}
        title={t("nav.search")}
        shouldFilter={false}
        className="sm:max-w-[1400px] h-[80vh]"
        value={selectedValue}
        onValueChange={setSelectedValue}
      >
        <ArchiveSearchPalette
          key={openCount}
          initialQuery={initialQuery}
          selectedValue={selectedValue}
          onSelectedValueChange={setSelectedValue}
          onClose={() => setIsOpen(false)}
        />
      </CommandDialog>
    </ArchiveSearchDialogContext>
  );
}

// Keyword and semantic hits are the default and need no badge; only an attachment hit is worth naming.
const badgeTypes = (types?: readonly string[] | null) =>
  (types ?? []).filter((type) => type !== "keyword" && type !== "semantic");

function ArchiveSearchPalette({
  initialQuery,
  selectedValue,
  onSelectedValueChange,
  onClose,
}: {
  initialQuery: string;
  selectedValue: string;
  onSelectedValueChange: (value: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tenantPaused, user } = useProfile();
  const { recent, remember, remove, clear } = useRecentSearches(user.id);
  const { search, results, loading, error, called, lastQuery, askAssistant, escalating } =
    useArchiveSearch();
  const [query, setQuery] = useState(initialQuery);
  // Starts equal to the initial query, so an opening with a query searches at once.
  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    search(debouncedQuery);
  }, [debouncedQuery, search]);

  const hasQuery = query.trim().length >= 2;

  // A search that found something is worth recalling even when the popup is
  // closed without opening a hit; saved once, on close (the palette remounts per
  // opening), so half-typed words along the way never land in the list.
  const lastFruitfulQuery = useRef<string | null>(null);
  if (called && !loading && !error && results.length > 0) lastFruitfulQuery.current = lastQuery;
  const rememberRef = useRef(remember);
  rememberRef.current = remember;
  useEffect(
    () => () => {
      if (lastFruitfulQuery.current) rememberRef.current(lastFruitfulQuery.current);
    },
    [],
  );

  // New results replace a highlight that is no longer among them with the top hit.
  const firstResultId = results[0]?.conversationId;
  const selectionIsStale =
    selectedValue !== ASK_ASSISTANT_VALUE &&
    !results.some((result) => result.conversationId === selectedValue);
  useEffect(() => {
    if (selectionIsStale && firstResultId) onSelectedValueChange(firstResultId);
  }, [selectionIsStale, firstResultId, onSelectedValueChange]);

  // The preview follows the highlighted result and keeps the last one while the
  // assistant row is highlighted or a new search is loading.
  const selectedResult = results.find(
    (result) => result.conversationId === selectedValue,
  );
  const [preview, setPreview] = useState<PreviewTarget | null>(null);
  if (
    selectedResult &&
    (selectedResult.conversationId !== preview?.conversationId ||
      (selectedResult.matchedMessageId ?? null) !== preview?.matchedMessageId)
  ) {
    setPreview({
      conversationId: selectedResult.conversationId,
      matchedMessageId: selectedResult.matchedMessageId ?? null,
    });
  }

  const showResultCount =
    hasQuery && called && !loading && !error && results.length > 0;

  const openConversation = (conversationId: string, matchedMessageId?: string | null) => {
    remember(query);
    void navigate(agentConversationPath(conversationId, matchedMessageId));
    onClose();
  };

  const renderState = () => {
    if (!query.trim() && recent.length > 0) {
      return (
        <CommandGroup
          heading={
            <div className="flex items-center justify-between">
              {t("archiveSearch.recent")}
              <button
                type="button"
                className="rounded px-1 text-xs font-normal hover:text-foreground"
                onClick={clear}
              >
                {t("archiveSearch.clearRecent")}
              </button>
            </div>
          }
        >
          {recent.map((q) => (
            <CommandItem
              key={q}
              value={`recent:${q}`}
              onSelect={() => setQuery(q)}
              className={PALETTE_ITEM_CLASS + " group"}
            >
              <History className="mr-2 !h-4 !w-4 text-muted-foreground" />
              <span className="flex-1 truncate">{q}</span>
              <button
                type="button"
                aria-label={t("archiveSearch.removeRecent")}
                className="rounded p-0.5 text-muted-foreground opacity-0 hover:text-foreground group-data-[selected=true]:opacity-100"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(q);
                }}
              >
                <X className="!h-3.5 !w-3.5" />
              </button>
            </CommandItem>
          ))}
        </CommandGroup>
      );
    }
    if (!hasQuery || !called) {
      return (
        <p className="px-4 py-6 text-center text-sm text-muted-foreground">
          {t("archiveSearch.hint")}
        </p>
      );
    }
    if (loading) {
      return (
        <div className="flex items-center justify-center gap-2 px-4 py-6 text-sm text-muted-foreground">
          <Throbber className="h-4 w-4" />
          {t("archiveSearch.searching")}
        </div>
      );
    }
    if (error) {
      return (
        <p className="px-4 py-6 text-center text-sm text-muted-foreground">
          {error.message}
        </p>
      );
    }
    if (results.length === 0) {
      return (
        <p className="px-4 py-6 text-center text-sm text-muted-foreground">
          {t("archiveSearch.empty")}
        </p>
      );
    }
    return (
      <CommandGroup>
        {results.map((result) => (
          <CommandItem
            key={result.conversationId}
            value={result.conversationId}
            onSelect={() =>
              openConversation(result.conversationId, result.matchedMessageId)
            }
            className={PALETTE_ITEM_CLASS + " flex-col items-stretch gap-0.5"}
          >
            <span className="truncate font-medium">
              {result.title ?? t("tickets.meta.noSubject")}
            </span>
            {(result.subtitle ?? result.dateLabel) && (
              <p className="truncate text-xs text-muted-foreground">
                {[result.subtitle, result.dateLabel].filter(Boolean).join(" • ")}
              </p>
            )}
            {result.snippet && (
              <p className="line-clamp-2 text-xs text-muted-foreground">
                {renderSnippet(result.snippet)}
              </p>
            )}
            {badgeTypes(result.matchTypes).length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1.5">
                {badgeTypes(result.matchTypes).map((type) => {
                  const meta = MATCH_TYPE_META[type];
                  if (!meta) return null;
                  const Icon = meta.icon;
                  return (
                    <span
                      key={type}
                      className={cn(
                        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium",
                        meta.className,
                      )}
                    >
                      {/* The palette sizes every item icon up; badges stay small. */}
                      <Icon className="!h-3 !w-3" />
                      {meta.label}
                    </span>
                  );
                })}
              </div>
            )}
          </CommandItem>
        ))}
      </CommandGroup>
    );
  };

  return (
    <>
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder={t("archiveSearch.placeholder")}
        className="pr-8"
      />
      <div className="flex min-h-0 flex-1">
        <div className="flex w-[42%] min-w-[360px] flex-col border-r">
          {showResultCount && (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              {t("archiveSearch.resultCount", { count: results.length })}
            </div>
          )}
          <CommandList className="max-h-none flex-1">
            {renderState()}
            {/* Same text as a real assistant turn, so a paused trial, which has no
                assistant, is not offered it. */}
            {!tenantPaused && lastQuery && hasQuery && !loading && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    value={ASK_ASSISTANT_VALUE}
                    className={PALETTE_ITEM_CLASS}
                    disabled={escalating}
                    onSelect={() => {
                      remember(query);
                      void askAssistant().then((started) => {
                        if (started) onClose();
                      });
                    }}
                  >
                    {escalating ? (
                      <Throbber className="mr-2 h-4 w-4" />
                    ) : (
                      <MessageCircleQuestion className="mr-2 h-4 w-4" />
                    )}
                    {t("archiveSearch.askAssistant")}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </div>
        <div className="min-w-0 flex-1 bg-muted/20">
          {preview ? (
            // Same read-only thread as the archive view; hideEditor renders the
            // Ticket at its natural height, so this wrapper owns the scrolling.
            <div className="h-full overflow-y-auto">
              <Suspense
                fallback={
                  <div className="flex h-full items-center justify-center">
                    <Throbber className="h-6 w-6 text-muted-foreground" />
                  </div>
                }
              >
                <Ticket
                  key={preview.conversationId}
                  ticketId={preview.conversationId}
                  hideEditor={true}
                  highlightedMessageId={preview.matchedMessageId ?? undefined}
                  chatBubbleLayout={true}
                />
              </Suspense>
            </div>
          ) : (
            <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
              {t("archiveSearch.previewHint")}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-5 border-t px-4 py-2 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <kbd className={KBD_CLASS}>↵</kbd>
          {t("archiveSearch.openHint")}
        </span>
        <span className="flex items-center gap-1.5">
          <kbd className={KBD_CLASS}>↑↓</kbd>
          {t("archiveSearch.navigateHint")}
        </span>
        <span className="flex items-center gap-1.5">
          <kbd className={KBD_CLASS}>Esc</kbd>
          {t("archiveSearch.closeHint")}
        </span>
      </div>
    </>
  );
}
